import React, { createContext } from 'react'
import { Form, Formik } from 'formik'
import useRecaptcha from 'components/Recaptcha/hooks/useRecaptcha'
import Recaptcha from 'components/Recaptcha'
import FormFieldControl from 'components/FormFieldControl'
import PasswordFieldC from 'components/FormFieldControl/PasswordFieldC'
import DateBirth from 'components/FormFieldControl/DateBirth'
import PhoneInputField from 'components/FormFieldControl/PhoneInputField'
import CountryField from 'components/FormFieldControl/CountryField'
import CurrencyField from 'components/FormFieldControl/CurrencyField'
import LanguageField from 'components/FormFieldControl/LanguageField'
import GenderField from 'components/FormFieldControl/GenderField'
import TermAndCondition from 'components/FormFieldControl/TermAndCondition'
import { UserRegisterButton } from 'components/Buttons'
import useSignupModalFlow from '../hooks/useSignupModalFlow'
import useSignupTermsAndConditions from 'hooks/useSignupTermsAndConditions'
import {
  emailVerificationSchema,
  completeSignupSchema,
  completeSignupSchemaEN
} from '../schemas/signupModalSchema'
import { getItem } from 'utils/storageUtils'
import { getAllowedCountryCodes, getAllowedInitialCountryCode, getDefaultSignupCurrency } from 'utils/signup'
import Icon from 'icons/Icon'
import { AUTH_MODAL_STEPS } from 'constants/general'
import { toast } from 'components/Toast'

// Create a context for form fields that need it
const SignupFormContext = createContext()

const SignupModalForm = () => {
  const {
    currentStep,
    verifiedEmail,
    loading,
    regFields,
    tenantDetails,
    generalConfig,
    allCountries,
    languageData,
    language,
    selectedLanguageCode,
    languageCode,
    languages,
    setLanguage,
    disableSubmit,
    handleEmailVerification,
    handleCompleteSignup,
    goBackToEmailStep,
    showStar,
    search,
    setSearch,
    countryCode
  } = useSignupModalFlow()

  const { recaptchaRef, handleRecaptchaChange, getRecaptchaToken } = useRecaptcha()

  const { defaultConditions, acceptConditions, setAcceptConditions } = useSignupTermsAndConditions()

  // Step 1: Email verification form
  if (currentStep === AUTH_MODAL_STEPS.EMAIL_VERIFICATION) {
    return (
      <section className='login-signup-modal'>
        <Formik
          enableReinitialize
          initialValues={{ email: verifiedEmail || '' }}
          validationSchema={emailVerificationSchema(languageData)}
          onSubmit={handleEmailVerification}
        >
          {(formik) => {
            return (
              <Form className='auth-form-modal'>
                <FormFieldControl
                  type='email'
                  name='email'
                  control='input'
                  label='loginEmail'
                  extraLabel='loginUsername'
                  placeholder={languageData?.emailPlaceholder}
                  containerClassName='custom-form-group'
                  fieldClassName='form-control custom-input'
                  showLabel={false}
                />

                <UserRegisterButton
                  formik={formik}
                  loading={disableSubmit || loading}
                  spinner
                  text={`${languageData?.signupFormButtonNext || 'Next'}`}
                  className='btn-hover-effect login-btn'
                  disabled={disableSubmit || loading}
                  disabledClassName=''
                />
              </Form>
            )
          }}
        </Formik>
      </section>
    )
  }

  // Step 2: Complete signup form
  // Only pre-fill the geo-detected country if it survived the allowedCountryMapping
  // filter (i.e. it is actually selectable). Otherwise leave it empty so a restricted
  // country (e.g. NL on wunderwins) is never silently pre-submitted.
  const allowedCountryCodes = getAllowedCountryCodes(allCountries)
  const allowedCountryCode = getAllowedInitialCountryCode(countryCode, allCountries)
  const activeLanguage = selectedLanguageCode || languageCode || language || getItem('language')

  const initialValues = {
    email: verifiedEmail,
    password: '',
    username: '',
    firstName: '',
    lastName: '',
    phone: null,
    phoneCode: '',
    dateOfBirth: null,
    address: '',
    confirmPassword: '',
    countryCode: allowedCountryCode,
    currencyCode: getDefaultSignupCurrency({
      activeLanguage,
      allowedCountryCode,
      tenantDetails,
      generalConfig
    }),
    gender: '',
    nationality: '',
    preferredLanguage: '',
    newsLetter: defaultConditions.newsLetter,
    sms: defaultConditions.sms,
    newsletterAndSms: defaultConditions.newsletterAndSms,
    zipCode: '',
    city: '',
    affiliateCode: ''
  }

  const validationSchema = () => {
    return [' ', '', 'EN'].includes(getItem('language')?.toUpperCase())
      ? completeSignupSchemaEN(regFields, languageData, allowedCountryCodes)
      : completeSignupSchema(regFields, languageData, allowedCountryCodes)
  }

  const onSubmit = async (formValues) => {
    // Capture recaptcha if enabled
    const token = await getRecaptchaToken()

    if (process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY && !token) {
      toast(languageData?.recaptchaError || 'Please verify you are not a robot', 'error')
      return
    }

    const result = await handleCompleteSignup({ ...formValues, recaptchaToken: token }, acceptConditions)
    // Handle errors if needed
    if (result?.error === 'username') {
      // Error already shown in toast
    }
  }

  // Common props for all form fields
  const commonFieldProps = {
    containerClassName: 'custom-form-group',
    fieldClassName: 'form-control custom-input',
    showLabel: false,
    showStar,
    regFields
  }

  // Context value for form fields that need it
  const contextValueForFields = {
    regFields,
    tenantDetails,
    generalConfig,
    allCountries,
    languageData,
    language,
    selectedLanguageCode,
    languages,
    setLanguage,
    showStar,
    search,
    setSearch,
    countryCode
  }

  const PreferredLanguageLabel = () => (
    <label htmlFor='preferredLanguage' className='custom-label'>
      {languageData?.signupLanguage || 'Preferred Language'}
      {regFields?.preferredLanguage === 2 ? showStar(regFields.preferredLanguage) : ''}
    </label>
  )

  return (
    <section className='login-signup-modal signup-modal-step-2'>
      <Icon name='back-arrow' className='back-arrow-icon' onClick={goBackToEmailStep} />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount
        validateOnBlur
        validateOnChange
        onSubmit={onSubmit}
      >
        {(formik) => {
          return (
            <Form className='auth-form-modal signup-complete-form'>
              {regFields && (
                <SignupFormContext.Provider value={contextValueForFields}>
                  <div className='div-2'>
                    {/* Email (disabled - verified in step 1) */}
                    {/* <FormFieldControl
                      type='email'
                      name='email'
                      control='input'
                      label='signupEmailAddress'
                      placeholder={languageData?.emailPlaceholder}
                      disabled
                      {...commonFieldProps}
                    /> */}

                    {/* Username */}
                    <FormFieldControl
                      type='text'
                      name='username'
                      control='input'
                      label='signupUserName'
                      placeholder={languageData?.userNamePlaceholder}
                      showInfoIcon
                      {...commonFieldProps}
                    />

                    {/* Password */}
                    <PasswordFieldC
                      name='password'
                      label='loginPassword'
                      placeholder={languageData?.passwordPlaceholder}
                      formik={formik}
                      {...commonFieldProps}
                    />

                    {/* Confirm Password */}
                    <PasswordFieldC
                      name='confirmPassword'
                      placeholder={languageData?.confirmPasswordPlaceholder}
                      label='signupConfirm'
                      extraLabel='loginPassword'
                      formik={formik}
                      {...commonFieldProps}
                    />

                    {/* First Name */}
                    <FormFieldControl
                      type='text'
                      name='firstName'
                      control='input'
                      label='signupFirstName'
                      placeholder={languageData?.firstNamePlaceholder}
                      {...commonFieldProps}
                    />

                    {/* Last Name */}
                    <FormFieldControl
                      type='text'
                      name='lastName'
                      control='input'
                      label='signupLastName'
                      placeholder={languageData?.lastNamePlaceholder}
                      {...commonFieldProps}
                    />

                    {/* Date of Birth */}
                    <FormFieldControl
                      name='dateOfBirth'
                      label='signupDob'
                      {...commonFieldProps}
                      regFields={{ ...regFields, city: 2, zipCode: 2 }}
                      languageData={languageData}
                    >
                      <DateBirth formik={formik} contextValue={SignupFormContext} />
                    </FormFieldControl>

                    {/* Address */}
                    <FormFieldControl
                      type='text'
                      name='address'
                      control='input'
                      label='signupAddress'
                      placeholder={languageData?.addressPlaceholder}
                      {...commonFieldProps}
                    />

                    {/* Phone */}
                    <FormFieldControl
                      name='phone'
                      label='signupPhoneNo'
                      {...commonFieldProps}
                      regFields={{ ...regFields, city: 2, zipCode: 2 }}
                      languageData={languageData}
                    >
                      <PhoneInputField formik={formik} contextValue={SignupFormContext} />
                    </FormFieldControl>

                    {/* City */}
                    <FormFieldControl
                      type='text'
                      name='city'
                      control='input'
                      label='signupCity'
                      placeholder={languageData?.cityPlaceholder}
                      {...commonFieldProps}
                      regFields={{ ...regFields, city: 2, zipCode: 2 }}
                      languageData={languageData}
                    />

                    {/* Zip Code */}
                    <FormFieldControl
                      type='text'
                      name='zipCode'
                      control='input'
                      label='signupPostcode'
                      placeholder={languageData?.postCodePlaceholder}
                      {...commonFieldProps}
                      regFields={{ ...regFields, city: 2, zipCode: 2 }}
                      languageData={languageData}
                    />

                    {/* Country */}
                    <FormFieldControl
                      name='countryCode'
                      label='signupCounty'
                      {...commonFieldProps}
                      regFields={{ ...regFields, city: 2, zipCode: 2 }}
                      languageData={languageData}
                    >
                      <CountryField formik={formik} contextValue={SignupFormContext} />
                    </FormFieldControl>

                    {/* Currency */}
                    <FormFieldControl
                      name='currencyCode'
                      label='signupCurrency'
                      {...commonFieldProps}
                      regFields={{ ...regFields, city: 2, zipCode: 2 }}
                      languageData={languageData}
                    >
                      <CurrencyField formik={formik} contextValue={SignupFormContext} />
                    </FormFieldControl>

                    {/* Preferred Language */}
                    <FormFieldControl
                      name='preferredLanguage'
                      label='signupLanguage'
                      {...commonFieldProps}
                      regFields={{ ...regFields, city: 2, zipCode: 2 }}
                      languageData={languageData}
                      defaultLabel={<PreferredLanguageLabel />}
                    >
                      <LanguageField formik={formik} contextValue={SignupFormContext} />
                    </FormFieldControl>

                    {/* Affiliate Code */}
                    <FormFieldControl
                      type='text'
                      control='input'
                      name='affiliateCode'
                      label='affiliateCode'
                      placeholder={languageData?.affiliateCodePlaceholder}
                      {...commonFieldProps}
                    />

                    {/* Gender */}
                    <FormFieldControl
                      name='gender'
                      label='signupGender'
                      {...commonFieldProps}
                      regFields={{ ...regFields, city: 2, zipCode: 2 }}
                      languageData={languageData}
                    >
                      <GenderField formik={formik} contextValue={SignupFormContext} />
                    </FormFieldControl>

                    {/* Terms & Conditions */}
                  </div>
                  <TermAndCondition
                    formik={formik}
                    acceptConditions={acceptConditions}
                    setAcceptConditions={setAcceptConditions}
                    contextValue={SignupFormContext}
                  />
                </SignupFormContext.Provider>
              )}

              <Recaptcha
                innerRef={recaptchaRef}
                onChange={handleRecaptchaChange}
              />

              <UserRegisterButton
                formik={formik}
                loading={disableSubmit || loading}
                spinner
                text={`${languageData?.signupRegister || 'Register'}`}
                className='btn-hover-effect login-btn'
                disabled={disableSubmit || loading}
                disabledClassName=''
              />
            </Form>
          )
        }}
      </Formik>
    </section>
  )
}

export default SignupModalForm
